import React, { createContext, useContext, useRef, useCallback } from 'react';

interface ScrollContextType {
  saveScrollPosition: (key: string, position: number) => void;
  getScrollPosition: (key: string) => number;
  restoreScrollPosition: (key: string, element: HTMLElement) => void;
}

const ScrollContext = createContext<ScrollContextType | null>(null);

export const ScrollProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const scrollPositions = useRef<Map<string, number>>(new Map());

  const saveScrollPosition = useCallback((key: string, position: number) => {
    scrollPositions.current.set(key, position);
  }, []);

  const getScrollPosition = useCallback((key: string): number => {
    return scrollPositions.current.get(key) || 0;
  }, []);

  const restoreScrollPosition = useCallback((key: string, element: HTMLElement) => {
    const position = scrollPositions.current.get(key) || 0;
    if (position > 0) {
      element.scrollTop = position;
    }
  }, []);

  return (
    <ScrollContext.Provider value={{ saveScrollPosition, getScrollPosition, restoreScrollPosition }}>
      {children}
    </ScrollContext.Provider>
  );
};

export const useScrollContext = () => {
  const context = useContext(ScrollContext);
  if (!context) {
    throw new Error('useScrollContext must be used within ScrollProvider');
  }
  return context;
};
